import assign from "lodash.assign";
import omit from "lodash.omit";
import { setErrorInErrors, clearErrorFromErrors } from "utils/errorsUtil";
import {
    MIN_NUM_SELF_SERVE_BULK_SUBSCRIBERS,
    MAX_NUM_SELF_SERVE_BULK_SUBSCRIBERS,
    SCREENS,
    OFFER_TYPES,
    ELOQUA_ABANDONED_CART_STATUS
} from "common/constants/Constants";
import GoogleTracking from "services/GoogleTracking";
import User from "services/User";
import { THINKING_TYPE_PROCESSING } from "common/constants/v2/Constants";
import { browserHistory } from "react-router";
import appPath from "utils/appPath";
import Entitlements from "services/Entitlements";
import Payments from "services/Payments";
import { setThinking, doneThinking } from "./thinking";
import marketingActions from "ducks/marketing";
import PayPal from "services/PayPal";

// Actions (must be name-spaced for reducer)

export const BULK_SUBSCRIPTION_FORM_SET_COMPANY_NAME = "BULK_SUBSCRIPTION_FORM_SET_COMPANY_NAME";
export const BULK_SUBSCRIPTION_FORM_SET_NUMBER_OF_USERS = "BULK_SUBSCRIPTION_FORM_SET_NUMBER_OF_USERS";
export const BULK_SUBSCRIPTION_FORM_SET_FIRST_NAME = "BULK_SUBSCRIPTION_FORM_SET_FIRST_NAME";
export const BULK_SUBSCRIPTION_FORM_SET_LAST_NAME = "BULK_SUBSCRIPTION_FORM_SET_LAST_NAME";
export const BULK_SUBSCRIPTION_FORM_SET_EMAIL = "BULK_SUBSCRIPTION_FORM_SET_EMAIL";
export const BULK_SUBSCRIPTION_FORM_SET_ERROR = "BULK_SUBSCRIPTION_FORM_SET_ERROR";
export const BULK_SUBSCRIPTION_FORM_CLEAR_ERROR = "BULK_SUBSCRIPTION_FORM_CLEAR_ERROR";
export const BULK_SUBSCRIPTION_FORM_SET_PRODUCT_DETAILS = "BULK_SUBSCRIPTION_FORM_SET_PRODUCT_DETAILS";
export const BULK_SUBSCRIPTION_FORM_SET_PURCHASED_SUBSCRIPTION_ID = "BULK_SUBSCRIPTION_FORM_SET_PURCHASED_SUBSCRIPTION_ID";

export function bulkSubscriptionFormReducer(state = { errors: {}, ratePlan: {} }, action) {
    switch (action.type) {
        case BULK_SUBSCRIPTION_FORM_SET_COMPANY_NAME:
            return assign({}, state, {
                companyName: action.companyName
            });
        case BULK_SUBSCRIPTION_FORM_SET_NUMBER_OF_USERS:
            return assign({}, state, {
                numberOfUsers: action.numberOfUsers
            });
        case BULK_SUBSCRIPTION_FORM_SET_FIRST_NAME:
            return assign({}, state, {
                firstName: action.firstName
            });
        case BULK_SUBSCRIPTION_FORM_SET_LAST_NAME:
            return assign({}, state, {
                lastName: action.lastName
            });
        case BULK_SUBSCRIPTION_FORM_SET_EMAIL:
            return assign({}, state, {
                email: action.email
            });
        case BULK_SUBSCRIPTION_FORM_SET_PRODUCT_DETAILS:
            return assign({}, state, {
                ratePlan: action.ratePlan
            });
        case BULK_SUBSCRIPTION_FORM_SET_PURCHASED_SUBSCRIPTION_ID:
            return assign({}, state, {
                purchasedSubscriptionId: action.purchasedSubscriptionId
            });
        case BULK_SUBSCRIPTION_FORM_SET_ERROR:
            return assign({}, state, {
                errors: setErrorInErrors(state.errors, action.error)
            });
        case BULK_SUBSCRIPTION_FORM_CLEAR_ERROR:
            return assign({}, state, {
                errors: clearErrorFromErrors(state.errors, action.errorType)
            });
        default:
            return state;
    }
}

export function setCompanyName(companyName) {
    return {
        type: BULK_SUBSCRIPTION_FORM_SET_COMPANY_NAME,
        companyName
    };
}

export function setNumberOfUsers(numberOfUsers) {
    // Explicitly make numberOfUsers undefined when it is empty string so that bulkSubscriptionPricing works
    return {
        type: BULK_SUBSCRIPTION_FORM_SET_NUMBER_OF_USERS,
        numberOfUsers: numberOfUsers || undefined
    };
}

export function setFirstName(firstName) {
    return {
        type: BULK_SUBSCRIPTION_FORM_SET_FIRST_NAME,
        firstName
    };
}

export function setLastName(lastName) {
    return {
        type: BULK_SUBSCRIPTION_FORM_SET_LAST_NAME,
        lastName
    };
}

export function setEmail(email) {
    return {
        type: BULK_SUBSCRIPTION_FORM_SET_EMAIL,
        email
    };
}

export function setProductDetails(ratePlan) {
    return {
        type: BULK_SUBSCRIPTION_FORM_SET_PRODUCT_DETAILS,
        ratePlan
    };
}

export function setPurchasedSubscriptionId(purchasedSubscriptionId) {
    return {
        type: BULK_SUBSCRIPTION_FORM_SET_PURCHASED_SUBSCRIPTION_ID,
        purchasedSubscriptionId
    };
}

export function setError(error) {
    GoogleTracking.registerSubscriptionEvent("error", `error:[${ error.message }]`);

    return {
        type: BULK_SUBSCRIPTION_FORM_SET_ERROR,
        error
    };
}

export function clearError(errorType) {
    return {
        type: BULK_SUBSCRIPTION_FORM_CLEAR_ERROR,
        errorType
    };
}

export function setAndValidateCompanyName(companyName) {
    return (dispatch) => {
        dispatch(setCompanyName(companyName));

        if (!companyName) {
            return dispatch(setError({
                type: "companyName",
                message: "Group name cannot be left blank"
            }));
        }

        dispatch(clearError("companyName"));
    };
}

export function setAndValidateNumberOfUsers(numberOfUsers) {
    return (dispatch) => {
        dispatch(setNumberOfUsers(numberOfUsers));

        if (!numberOfUsers) {
            return dispatch(setError({
                type: "numberOfUsers",
                message: "Number of users cannot be left blank"
            }));
        }

        const numberOfUsersValue = parseInt(numberOfUsers);

        if (numberOfUsersValue > MAX_NUM_SELF_SERVE_BULK_SUBSCRIBERS) {
            return dispatch(setError({
                type: "numberOfUsers",
                message: `Number must be less than ${ MAX_NUM_SELF_SERVE_BULK_SUBSCRIBERS + 1 }`
            }));
        }

        if (numberOfUsersValue < MIN_NUM_SELF_SERVE_BULK_SUBSCRIBERS) {
            return dispatch(setError({
                type: "numberOfUsers",
                message: `Must have at least ${ MIN_NUM_SELF_SERVE_BULK_SUBSCRIBERS } users`
            }));
        }

        dispatch(clearError("numberOfUsers"));
    };
}

export function setAndValidateFirstName(firstName) {
    return (dispatch) => {
        dispatch(setFirstName(firstName));

        if (!firstName) {
            return dispatch(setError({
                type: "firstName",
                message: "First name cannot be left blank"
            }));
        }

        dispatch(clearError("firstName"));
    };
}

export function setAndValidateLastName(lastName) {
    return (dispatch) => {
        dispatch(setLastName(lastName));

        if (!lastName) {
            return dispatch(setError({
                type: "lastName",
                message: "Last name cannot be left blank"
            }));
        }

        dispatch(clearError("lastName"));
    };
}

export function getProductDetails(id) {
    return (dispatch, getState) => {
        return Entitlements.getProductRatePlanInformation(id)
            .then((response) => {
                const { ratePlan } = response;

                if (ratePlan.offerType !== OFFER_TYPES.GROUP) {
                    return window.location.replace(`${ getState().config.bloombergSiteUrl }/subscriptions`);
                }

                dispatch(setProductDetails(ratePlan));
            })
            .catch((response) => {
                return window.location.replace(response.redirectTo);
            });
    };
}

export function submitSubscription() {
    return (dispatch, getState) => {
        const {
            loginRegister = {},
            user = {},
            paymentForm = {},
            billingAddressForm,
            bulkSubscriptionForm
        } = getState();

        const {
            firstName,
            lastName
        } = bulkSubscriptionForm;

        const email = user.email || loginRegister.email;

        if (user.email) {
            User.updateName(firstName, lastName);
        }

        GoogleTracking.registerSubscriptionEvent("submit", "purchase:subscription");

        dispatch(setThinking(THINKING_TYPE_PROCESSING));

        dispatch(clearError());

        return dispatch(preauthorizeCardPayment())
            .then(() => Entitlements.addBloombergSubscription({
                paymentForm: omit(paymentForm, "stripe"), // we don't want to send the whole stripe DOM element
                billingAddressForm,
                bulkSubscriptionForm,
                email
            }))
            .then(({ isNew, alreadyHasSubscription, subscriptionId }) => {
                if (!alreadyHasSubscription) {
                    GoogleTracking.registerEvent({
                        event: "Keywee Event",
                        keyweeEventName: "Purchase"
                    });
                    GoogleTracking.registerSubscriptionEvent("success", "purchase:completed");
                }

                dispatch(marketingActions.updateAbandonedCartStatus(email, ELOQUA_ABANDONED_CART_STATUS.SUBSCRIBE_SUCCESS));
                dispatch(doneThinking());
                dispatch(setPurchasedSubscriptionId(subscriptionId));

                if (isNew) {
                    return browserHistory.push(appPath(SCREENS.SUBSCRIPTION_ACTIVATION_EMAIL, {
                        pathname: window.location.pathname,
                        search: `?redirectTo=${ SCREENS.SUBSCRIPTION_SUCCESS }&isBulk=true&subscriptionId=${ subscriptionId }`
                    }));
                }

                return browserHistory.push(appPath(SCREENS.SUBSCRIPTION_SUCCESS, {
                    pathname: window.location.pathname,
                    search: `?existing=true&signedIn=${ !!user.email }&alreadyHasSubscription=${ alreadyHasSubscription }&isBulk=true&subscriptionId=${ subscriptionId }`
                }));
            })
            .catch((error) => {
                dispatch(setError({ type: "purchaseSubscription", message: error.errorMessage }));
                dispatch(doneThinking());
            });
    };
}

function preauthorizeCardPayment() {
    return (_, getState) => {
        const {
            paymentForm = {},
            bulkSubscriptionForm = {}
        } = getState();

        const {
            cardInformation: {
                customer: customerId,
                id: cardId
            } = {},
            paymentMethod,
            paypalId
        } = paymentForm;

        const {
            ratePlan: {
                ratePlanId
            } = {},
            numberOfUsers
        } = bulkSubscriptionForm;

        if (paymentMethod === "stripe") {
            return Payments.addPreAuthCharge(cardId, customerId, ratePlanId, numberOfUsers);
        } else if (paymentMethod === "paypal") {
            return PayPal.addPreAuthCharge(paypalId, ratePlanId, numberOfUsers);
        }

        return Promise.resolve();
    };
}

export default {
    setError,
    clearError,
    setCompanyName,
    setNumberOfUsers,
    setFirstName,
    setLastName,
    setEmail,
    setProductDetails,
    setPurchasedSubscriptionId,
    setAndValidateCompanyName,
    setAndValidateNumberOfUsers,
    setAndValidateFirstName,
    setAndValidateLastName,
    getProductDetails,
    submitSubscription
};
